/**
 * `/kelly-ai` is a deep link to a view state, not a second place Kelly lives. When it can promote,
 * it renders nothing and the full-screen dock covers the stage; when it cannot, it says so, because this route
 * has no page of its own and a blank rectangle is not a state.
 *
 * It cannot render its own `ConversationSurface`. Two surfaces on one controller means two `ComposerDraftBridge`s
 * writing one draft key, two `onMessagesChange` writers, and `useKellyChatSurfacePresence` — which assumes a
 * single caller — aborting one surface's live turn when the other unmounts.
 */

import { useEffect, useLayoutEffect, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Box, CircularProgress, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { useHaveEnabledFeaturesFailed } from "resources/account"
import { useAppDispatch, useAppSelector, useAppStore } from "hooks/redux"
import { selectDockView, setArtifactDeepLink, setDockView } from "./store/agent-chat-slice"
import type { DockView } from "./store/agent-chat-slice"
import { useKellyChatEntitlement, useKellyChatOptional } from "./chatbox/kelly-chat-context"
import "./style.scss"

const STATE_SX = { display: "grid", placeItems: "center", height: "100%", gap: 1, color: "text.secondary" } satisfies SxProps<Theme>

// Whether there is an in-app entry to go back to. `idx` is react-router's own position counter and an INTERNAL
// shape, hence the narrowing rather than a cast — a `replace` keeps it, so it stays 0 for an entry the app
// replaced into rather than navigated to.
function hasInAppHistoryBehind(): boolean {
  const state: unknown = window.history.state
  const index = typeof state === "object" && state !== null ? (state as { idx?: unknown }).idx : undefined
  return typeof index === "number" && index > 0
}

export default function KellyRouteBridge() {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const navigate = useNavigate()
  // From the URL, never the store: `useAccountAlias` lags the route by a round trip on an account switch, and
  // this component navigates.
  const { accountValue, resourceId } = useParams()
  const dockView = useAppSelector(selectDockView)
  const haveFeaturesFailed = useHaveEnabledFeaturesFailed()
  // Same guard the route made before it stopped rendering a surface: `withFeatureGate` renders this route
  // through while the feature list is in flight, and promoting is sticky activation — an un-entitled account
  // reaching that window would fire an un-abortable threads GET that poisons the shared cache.
  const entitlement = useKellyChatEntitlement()
  // Null once the gate's Sentry boundary has caught a Kelly crash: it renders the app bare, so there is no
  // controller and no dock either.
  const controller = useKellyChatOptional()

  // The only component that can read the resource id — the panel that opens it is mounted by the dock,
  // outside this route. Published for the route's lifetime, so a close and a navigation differ.
  useEffect(() => {
    dispatch(setArtifactDeepLink(resourceId ?? null))
    return () => { dispatch(setArtifactDeepLink(null)) }
  }, [resourceId, dispatch])

  // Promote, and hand the view back on the way out. One effect, so one token covers both directions;
  // `priorViewRef` is captured once so an entitlement flap cannot record `full` as the view to go back to.
  // Layout-phase, or a cold deep link paints a frame of empty stage before promoting.
  const priorViewRef = useRef<DockView | null>(null)
  const isSelfRestoreRef = useRef(false)
  useLayoutEffect(() => {
    if (entitlement !== "confirmed") return undefined
    const priorView = priorViewRef.current ?? store.getState().agentChat.dockView
    priorViewRef.current = priorView
    dispatch(setDockView("full"))
    return () => {
      // Only raise the token when the restore actually MOVES the view. Entering from `full` makes the dispatch
      // a no-op, so nothing re-renders, the effect below never runs to consume it, and the token sits armed
      // waiting to swallow the user's next Minimise — stranding them on a route with no page.
      if (store.getState().agentChat.dockView !== "full" || priorView === "full") return
      isSelfRestoreRef.current = true
      dispatch(setDockView(priorView))
    }
  }, [entitlement, dispatch, store])

  // Leaving full screen leaves the route with it: this route renders no page, so minimising would
  // otherwise strand the user on an empty shell. Back, not a destination — and "is there a page behind me" is
  // `idx`, never `location.key`: a `<Navigate replace>` (the legacy `/agent-chat` alias) mints a fresh key while
  // holding `idx`, so a bookmark to it would read as having history and make Minimise inert.
  const hasBeenFullRef = useRef(false)
  useLayoutEffect(() => {
    if (dockView === "full") {
      hasBeenFullRef.current = true
      // Back in full screen means any pending suppression is stale — bounding the token's lifetime to the
      // window where the restore it describes can still be observed.
      isSelfRestoreRef.current = false
      return
    }
    const isOurRestore = isSelfRestoreRef.current
    isSelfRestoreRef.current = false
    // One predicate for "the user took Kelly out of full screen while we owned this route". An account switch
    // resets the whole slice, which clears the sticky activation; a minimise never does.
    const isUserExit = hasBeenFullRef.current && !isOurRestore && store.getState().agentChat.hasActivated
    if (!isUserExit) return
    if (hasInAppHistoryBehind()) {
      navigate(-1)
      return
    }
    if (accountValue != null) navigate(`/${accountValue}`, { replace: true })
  }, [dockView, accountValue, navigate, store])

  if (entitlement === "confirmed" && controller != null) return null

  return (
    <div className="agent-chat-page">
      {controller == null ? (
        <Box sx={STATE_SX} role="status" data-testid="kelly-unavailable">
          <Typography variant="body2">Kelly is unavailable right now. Reload the page to try again.</Typography>
        </Box>
      ) : haveFeaturesFailed ? (
        <Box sx={STATE_SX} role="status" data-testid="kelly-entitlement-failed">
          <Typography variant="body2">We could not check whether this account has Kelly. Reload the page to try again.</Typography>
        </Box>
      ) : (
        <Box sx={STATE_SX} role="status" data-testid="kelly-entitlement-pending">
          <CircularProgress size={24} />
          <Typography variant="body2">Opening Kelly…</Typography>
        </Box>
      )}
    </div>
  )
}
